import { ApolloClient, ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { GlobalGtmRegistrations } from '@ps/googleTagManager';
import { GlobalIntercomArticle } from '@ps/intercom';
import { GlobalCookieBanner } from '@ps/legal';
import {
  CubicCalculator,
  PublicRatesCalculatorForm,
  PublicRatesCalculatorRateResults,
  RateResults,
} from '@ps/rateCalculators';
import { StrictMode, Suspense } from 'react';
import { Root, createRoot } from 'react-dom/client';
import { MemoryRouter } from 'react-router';
import initApolloClient from '../common/apollo/client';
import FlashMessages from '../common/components/FlashMessages';
import initLocale from '../common/locale';
import initGrid from '../common/styles/grid';
import initIcons from '../common/styles/icons';
import initValidation from '../common/validation';

initLocale('en-US');
initValidation();
initIcons();
initGrid();

declare global {
  interface Window {
    PublicRateCalculator?: {
      setGraphqlEndpoint: (endpoint: string) => void;
      renderForm: (rootEl: Element, onResults: (results: RateResults) => void) => () => void;
      renderResults: (rootEl: Element, results: RateResults) => () => void;
    };
    GroundAdvantageCubicCalculator?: { render: (rootEl: Element) => () => void };
    PriorityMailCubicCalculator?: { render: (rootEl: Element) => () => void };
    ParcelSelectGroundCubicCalculator?: { render: (rootEl: Element) => () => void };
    CookieBanner?: { render: (rootEl: Element) => () => void };
  }
}

const reactRoots = new WeakMap<Element, Root>();
let graphqlEndpoint: string;
let apolloClient: ApolloClient<NormalizedCacheObject>;

function getApolloClient() {
  if (!graphqlEndpoint) {
    throw new Error('GraphQL endpoint is not configured yet');
  }

  if (!apolloClient) {
    apolloClient = initApolloClient({ endpoint: graphqlEndpoint });
  }

  return apolloClient;
}

// Bind to window
window.PublicRateCalculator = {
  setGraphqlEndpoint(endpoint) {
    graphqlEndpoint = endpoint;
  },
  renderForm(rootEl, onResults) {
    const root = reactRoots.get(rootEl) ?? createRoot(rootEl);
    reactRoots.set(rootEl, root);

    root.render(
      <StrictMode>
        <MemoryRouter>
          <ApolloProvider client={getApolloClient()}>
            <FlashMessages />
            <Suspense fallback={null}>
              <GlobalIntercomArticle />
            </Suspense>
            <PublicRatesCalculatorForm onRateResults={onResults} />
          </ApolloProvider>
        </MemoryRouter>
      </StrictMode>,
    );

    return () => root.unmount();
  },
  renderResults(rootEl, results) {
    const root = reactRoots.get(rootEl) ?? createRoot(rootEl);
    reactRoots.set(rootEl, root);

    root.render(
      <StrictMode>
        <MemoryRouter>
          <ApolloProvider client={getApolloClient()}>
            <Suspense fallback={null}>
              <GlobalIntercomArticle />
            </Suspense>
            <PublicRatesCalculatorRateResults
              rateResults={results}
              scrollOnResultsChange
              onRegisterClick={() => {
                window.location.href = 'https://ship.pirateship.com/signup';
              }}
            />
          </ApolloProvider>
        </MemoryRouter>
      </StrictMode>,
    );

    return () => root.unmount();
  },
};

// Bind to window
window.PriorityMailCubicCalculator = {
  render(rootEl) {
    const root = reactRoots.get(rootEl) ?? createRoot(rootEl);
    reactRoots.set(rootEl, root);

    root.render(
      <StrictMode>
        <CubicCalculator mailClass="priorityMailCubic" />
      </StrictMode>,
    );

    return () => root.unmount();
  },
};
window.ParcelSelectGroundCubicCalculator = {
  // Still used on historical service page
  render(rootEl) {
    const root = reactRoots.get(rootEl) ?? createRoot(rootEl);
    reactRoots.set(rootEl, root);

    root.render(
      <StrictMode>
        <CubicCalculator mailClass="parcelSelectGroundCubic" />
      </StrictMode>,
    );

    return () => root.unmount();
  },
};
window.GroundAdvantageCubicCalculator = {
  render(rootEl) {
    const root = reactRoots.get(rootEl) ?? createRoot(rootEl);
    reactRoots.set(rootEl, root);

    root.render(
      <StrictMode>
        <CubicCalculator mailClass="groundAdvantageCubic" />
      </StrictMode>,
    );

    return () => root.unmount();
  },
};

window.CookieBanner = {
  render: (rootEl) => {
    const root = reactRoots.get(rootEl) ?? createRoot(rootEl);
    reactRoots.set(rootEl, root);

    root.render(
      <StrictMode>
        <MemoryRouter>
          <GlobalGtmRegistrations />
          <GlobalCookieBanner />
        </MemoryRouter>
      </StrictMode>,
    );

    return () => root.unmount();
  },
};

// Dispatch ready event
document.dispatchEvent(new Event('calculatorsReady')); // @deprecated
document.dispatchEvent(new Event('webflowComponentsReady')); // combined event for all webflow components
